/**
 * Low-latency piano synth.
 * Prefers AudioWorklet (Chromium native audio render thread);
 * falls back to main-thread oscillators if Worklet is unavailable.
 */

import { midiToFrequency, clampMidiNote } from "./midiMath.js";
import {
  PIANO_SYNTH_WORKLET_NAME,
  PIANO_SYNTH_WORKLET_SOURCE,
} from "./pianoSynthWorkletSource.js";

export interface SynthVoiceOptions {
  attack?: number;
  decay?: number;
  sustain?: number;
  release?: number;
  brightness?: number;
}

export type AudioBackendKind = "native" | "worklet" | "main-thread" | "none";

/** Optional low-latency sink (Android AudioTrack Capacitor plugin). */
export type NativeSynthSink = {
  noteOn: (midi: number, velocity: number) => void;
  noteOff: (midi: number) => void;
  allNotesOff: () => void;
  setMasterVolume: (value: number) => void;
  setMuted: (muted: boolean) => void;
};

export interface ActiveVoice {
  midiNote: number;
  oscillators: OscillatorNode[];
  gain: GainNode;
  startedAt: number;
  release: number;
}

const DEFAULT_ADSR: Required<SynthVoiceOptions> = {
  attack: 0,
  decay: 0.12,
  sustain: 0.55,
  release: 0.16,
  brightness: 0.7,
};

export class PianoSynthEngine {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private workletReady = false;
  private workletFailed = false;
  private voices = new Map<number, ActiveVoice>();
  private unlocked = false;
  private muted = false;
  private masterVolume = 0.75;
  private adsr: Required<SynthVoiceOptions> = { ...DEFAULT_ADSR };
  private sustainPedal = false;
  private pedalHeld = new Set<number>();
  private backend: AudioBackendKind = "none";
  private nativeSink: NativeSynthSink | null = null;

  get audioContext(): AudioContext | null {
    return this.ctx;
  }

  get masterOutput(): GainNode | null {
    return this.masterGain;
  }

  get isUnlocked(): boolean {
    return this.unlocked && (this.ctx !== null || this.nativeSink !== null);
  }

  get isReady(): boolean {
    return this.isUnlocked && !this.muted;
  }

  /** Prefer Android AudioTrack path when the Capacitor plugin is live. */
  setNativeSink(sink: NativeSynthSink | null): void {
    this.nativeSink = sink;
    if (sink) {
      this.unlocked = true;
      this.backend = "native";
      sink.setMasterVolume(this.masterVolume);
      sink.setMuted(this.muted);
    } else if (this.backend === "native") {
      this.backend = this.workletReady ? "worklet" : this.ctx ? "main-thread" : "none";
    }
  }

  get isMuted(): boolean {
    return this.muted;
  }

  get audioBackend(): AudioBackendKind {
    return this.backend;
  }

  get currentTime(): number {
    return this.ctx?.currentTime ?? 0;
  }

  get baseLatencySec(): number {
    if (this.backend === "native") return 0.002;
    return this.ctx?.baseLatency ?? 0;
  }

  /** OS / device output buffer only (excludes Web Audio baseLatency). */
  get outputBufferSec(): number {
    if (this.backend === "native") return 0.006;
    if (!this.ctx) return 0;
    const anyCtx = this.ctx as AudioContext & { outputLatency?: number };
    return anyCtx.outputLatency ?? 0;
  }

  get outputLatencySec(): number {
    if (this.backend === "native") {
      // AudioTrack PERFORMANCE_MODE_LOW_LATENCY is typically a few ms; expose a
      // modest estimate so exam timing compensation stays conservative.
      return 0.008;
    }
    return this.baseLatencySec + this.outputBufferSec;
  }

  /**
   * Synchronous graph create — safe to call inside keydown.
   * Worklet module load continues async; first notes may use main-thread fallback briefly.
   */
  unlockSync(): void {
    if (!this.ctx) {
      const AC =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      // Prefer the device native sample rate. Forcing 48 kHz can add
      // resampling latency on Android WebViews that prefer 44.1 kHz.
      // Numeric latencyHint asks Chromium for a smaller render quantum when
      // the OS allows it (falls back if unsupported).
      try {
        this.ctx = new AC({ latencyHint: 0.003 });
      } catch {
        this.ctx = new AC({ latencyHint: "interactive" });
      }
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = this.muted ? 0 : this.masterVolume;
      this.masterGain.connect(this.ctx.destination);
      this.unlocked = true;
      if (this.backend !== "native") this.backend = "main-thread";
      void this.tryInitWorklet();
    }
    if (this.ctx.state === "suspended") {
      void this.ctx.resume();
    }
    // Do NOT force unmute here — Mute must stay sticky until the user unmutes.
    if (this.masterGain && !this.muted) {
      this.masterGain.gain.value = this.masterVolume;
    }
  }

  private async tryInitWorklet(): Promise<void> {
    if (!this.ctx || !this.masterGain || this.workletReady || this.workletFailed) return;
    if (typeof this.ctx.audioWorklet?.addModule !== "function") {
      this.workletFailed = true;
      this.backend = "main-thread";
      return;
    }
    try {
      const blob = new Blob([PIANO_SYNTH_WORKLET_SOURCE], {
        type: "application/javascript",
      });
      const url = URL.createObjectURL(blob);
      await this.ctx.audioWorklet.addModule(url);
      URL.revokeObjectURL(url);
      this.workletNode = new AudioWorkletNode(this.ctx, PIANO_SYNTH_WORKLET_NAME, {
        numberOfInputs: 0,
        numberOfOutputs: 1,
        outputChannelCount: [2],
      });
      this.workletNode.connect(this.masterGain);
      this.workletNode.port.postMessage({ type: "master", value: this.masterVolume });
      this.workletNode.port.postMessage({ type: "mute", value: this.muted });
      this.workletReady = true;
      if (this.backend !== "native") this.backend = "worklet";
      // Leave any in-flight main-thread voices alone so a note started during
      // worklet init is not cut mid-attack. New notes already use the worklet.
    } catch {
      this.workletFailed = true;
      if (this.backend !== "native") this.backend = "main-thread";
      this.workletNode = null;
    }
  }

  async unlock(): Promise<void> {
    this.unlockSync();
    if (this.ctx?.state === "suspended") {
      await this.ctx.resume();
    }
    await this.tryInitWorklet();
    if (!this.ctx) return;
    const buffer = this.ctx.createBuffer(1, 1, this.ctx.sampleRate);
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.ctx.destination);
    source.start(0);
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.nativeSink) this.nativeSink.setMuted(muted);
    if (this.workletReady && this.workletNode) {
      this.workletNode.port.postMessage({ type: "mute", value: muted });
    }
    if (!this.masterGain || !this.ctx) return;
    this.masterGain.gain.setValueAtTime(muted ? 0 : this.masterVolume, this.ctx.currentTime);
    if (muted) this.allNotesOff();
  }

  setMasterVolume(volume01: number): void {
    this.masterVolume = Math.max(0, Math.min(1, volume01));
    if (this.nativeSink) this.nativeSink.setMasterVolume(this.masterVolume);
    if (this.workletReady && this.workletNode) {
      this.workletNode.port.postMessage({ type: "master", value: this.masterVolume });
    }
    if (this.masterGain && this.ctx && !this.muted) {
      this.masterGain.gain.setValueAtTime(this.masterVolume, this.ctx.currentTime);
    }
  }

  setAdsr(options: SynthVoiceOptions): void {
    this.adsr = { ...this.adsr, ...options };
  }

  noteOn(midiNote: number, velocity = 100, when?: number): void {
    this.unlockSync();
    if (this.muted) return;
    const note = clampMidiNote(midiNote);
    const vel = Math.max(1, Math.min(127, velocity));

    // Worklet / native paths ignore AudioContext `when` — delay the message so
    // PiecePlayer demos don't get noteOn+noteOff in the same tick (silent click).
    if (when != null && this.ctx && ((this.nativeSink && this.backend === "native") || (this.workletReady && this.workletNode))) {
      const delayMs = Math.max(0, (when - this.ctx.currentTime) * 1000);
      window.setTimeout(() => {
        if (this.muted) return;
        if (this.nativeSink && this.backend === "native") {
          this.nativeSink.noteOn(note, vel);
          return;
        }
        if (this.workletReady && this.workletNode) {
          this.workletNode.port.postMessage({ type: "noteOn", midi: note, velocity: vel });
        }
      }, delayMs);
      return;
    }

    if (this.nativeSink && this.backend === "native") {
      this.nativeSink.noteOn(note, vel);
      return;
    }

    if (!this.ctx || !this.masterGain) return;

    if (this.workletReady && this.workletNode) {
      this.workletNode.port.postMessage({
        type: "noteOn",
        midi: note,
        velocity: vel,
      });
      return;
    }

    // Main-thread fallback (and brief period before worklet finishes loading)
    this.noteOnMain(note, vel, when);
  }

  private noteOnMain(note: number, velocity: number, when?: number): void {
    if (!this.ctx || !this.masterGain) return;
    const vel = velocity / 127;
    const t = when ?? this.ctx.currentTime;
    const freq = midiToFrequency(note);
    const { decay, sustain, release } = this.adsr;

    this.killVoiceMain(note, t);

    const gain = this.ctx.createGain();
    const peak = vel * 0.85;
    gain.gain.setValueAtTime(peak, t);
    gain.gain.exponentialRampToValueAtTime(
      Math.max(0.0001, vel * sustain),
      t + Math.max(0.02, decay),
    );

    const osc = this.ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.value = freq;
    osc.connect(gain);

    const partial = this.ctx.createOscillator();
    partial.type = "sine";
    partial.frequency.value = freq * 2;
    const partialGain = this.ctx.createGain();
    partialGain.gain.value = 0.12 * vel;
    partial.connect(partialGain);
    partialGain.connect(gain);

    gain.connect(this.masterGain);
    osc.start(t);
    partial.start(t);

    this.voices.set(note, {
      midiNote: note,
      oscillators: [osc, partial],
      gain,
      startedAt: t,
      release,
    });
  }

  setSustainPedal(down: boolean): void {
    const was = this.sustainPedal;
    this.sustainPedal = down;
    if (this.workletReady && this.workletNode) {
      this.workletNode.port.postMessage({ type: "sustain", down });
      return;
    }
    if (was && !down) {
      for (const note of [...this.pedalHeld]) {
        this.pedalHeld.delete(note);
        this.noteOff(note);
      }
    }
  }

  get sustainPedalDown(): boolean {
    return this.sustainPedal;
  }

  noteOff(midiNote: number, _velocity = 0, when?: number): void {
    const note = clampMidiNote(midiNote);

    if (when != null && this.ctx && ((this.nativeSink && this.backend === "native") || (this.workletReady && this.workletNode))) {
      const delayMs = Math.max(0, (when - this.ctx.currentTime) * 1000);
      window.setTimeout(() => {
        if (this.nativeSink && this.backend === "native") {
          this.nativeSink.noteOff(note);
          return;
        }
        if (this.workletReady && this.workletNode) {
          this.workletNode.port.postMessage({ type: "noteOff", midi: note });
        }
      }, delayMs);
      return;
    }

    if (this.nativeSink && this.backend === "native") {
      this.nativeSink.noteOff(note);
      return;
    }

    if (!this.ctx) return;

    if (this.workletReady && this.workletNode) {
      this.workletNode.port.postMessage({ type: "noteOff", midi: note });
      return;
    }

    if (this.sustainPedal) {
      this.pedalHeld.add(note);
      return;
    }
    const voice = this.voices.get(note);
    if (!voice) return;

    const t = when ?? this.ctx.currentTime;
    const release = voice.release;
    try {
      voice.gain.gain.cancelScheduledValues(t);
      voice.gain.gain.setValueAtTime(Math.max(0.0001, voice.gain.gain.value), t);
      voice.gain.gain.exponentialRampToValueAtTime(0.0001, t + release);
    } catch {
      /* ignore */
    }

    const stopAt = t + release + 0.03;
    for (const osc of voice.oscillators) {
      try {
        osc.stop(stopAt);
      } catch {
        /* already stopped */
      }
    }

    this.voices.delete(note);
    const g = voice.gain;
    const oscs = voice.oscillators;
    window.setTimeout(() => {
      try {
        g.disconnect();
        for (const o of oscs) o.disconnect();
      } catch {
        /* disposed */
      }
    }, (release + 0.05) * 1000);
  }

  private killVoiceMain(note: number, t: number): void {
    const voice = this.voices.get(note);
    if (!voice) return;
    try {
      voice.gain.gain.cancelScheduledValues(t);
      voice.gain.gain.setValueAtTime(0, t);
      for (const osc of voice.oscillators) {
        try {
          osc.stop(t);
        } catch {
          /* already stopped */
        }
      }
      voice.gain.disconnect();
    } catch {
      /* disposed */
    }
    this.voices.delete(note);
  }

  allNotesOff(): void {
    // Force pedal up first so noteOff cannot re-queue voices into pedalHeld.
    this.sustainPedal = false;
    this.pedalHeld.clear();
    if (this.nativeSink && this.backend === "native") {
      this.nativeSink.allNotesOff();
    }
    if (this.workletReady && this.workletNode) {
      this.workletNode.port.postMessage({ type: "sustain", down: false });
      this.workletNode.port.postMessage({ type: "panic" });
    }
    for (const note of [...this.voices.keys()]) {
      this.killVoiceMain(note, this.ctx?.currentTime ?? 0);
    }
    this.voices.clear();
  }

  async suspend(): Promise<void> {
    if (this.ctx?.state === "running") await this.ctx.suspend();
  }

  async resume(): Promise<void> {
    if (this.ctx?.state === "suspended") await this.ctx.resume();
  }

  dispose(): void {
    this.allNotesOff();
    try {
      this.workletNode?.disconnect();
    } catch {
      /* */
    }
    this.workletNode = null;
    this.workletReady = false;
    void this.ctx?.close();
    this.ctx = null;
    this.masterGain = null;
    this.unlocked = false;
    this.muted = false;
    this.backend = "none";
  }
}
